import { HubConnection, HubConnectionBuilder } from "@microsoft/signalr";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { User } from "oidc-client-ts";

export const SignalRContext = createContext<any | null>(null);

export const SignalRProvider = ({ children }: { children: any }) => {
  const user = getUser();
  const token = user?.access_token;
  const [connection, setConnection] = useState<null | HubConnection>(null);
  const [connectionError, setConnectionError] = useState<string | null>(null);
  
  // Flag to enable/disable SignalR connection attempts
  const ENABLE_SIGNALR = false; // Set to true when your SignalR endpoint is available

  function getUser() {
    const oidcStorage = sessionStorage.getItem(
      `oidc.user:${process.env.REACT_APP_AUTH_APP}:${process.env.REACT_APP_CLIENT_ID}`
    );
    if (!oidcStorage) {
      return null;
    }
    return User.fromStorageString(oidcStorage);
  }

  useEffect(() => {
    let connectionSignalR: HubConnection | null = null;
    
    const connectToSignalR = async () => {
      if (!ENABLE_SIGNALR) {
        console.log('SignalR connections are currently disabled');
        return;
      }
      
      if (!token) {
        console.log('No authentication token available for SignalR');
        return;
      }
      
      try {
        setConnectionError(null);
        const BASE_URL = 'https://energyhub-api.processhub.ai';
        connectionSignalR = new HubConnectionBuilder()
          .withUrl(`${BASE_URL}/MainHub`, {
            accessTokenFactory: () => token,
          })
          .withAutomaticReconnect() // Add automatic reconnection
          .build();
          
        // Add connection event handlers
        connectionSignalR.onclose((error) => {
          console.log('SignalR connection closed', error);
        });
        
        await connectionSignalR.start();
        console.log('SignalR connection established successfully');
        setConnection(connectionSignalR);
      } catch (error: any) {
        // Only log once instead of multiple error messages
        const errorMsg = `Unable to connect to SignalR hub: ${error.message}`;
        console.warn(errorMsg);
        setConnectionError(errorMsg);
        setConnection(null);
      }
    };
    
    connectToSignalR();
    
    // Cleanup function
    return () => {
      if (connectionSignalR) {
        connectionSignalR.stop().catch(err => console.warn('Error stopping SignalR:', err));
      }
    };
  }, [token]);

  const FileUploadHandler = useCallback(
    (data: any) => {
      if (connection) {
        connection.on("GenericFunc", function (e) {
          data(e);
        });
      }
    },
    [connection]
  );


  const unityDataHandler = useCallback(
    (data: any) => {
      if (connection) {
        connection.on("TagValueChanged", function () {
          data();
        });
      }
    },
    [connection]
  );

  // The context now includes connection status information
  return (
    <SignalRContext.Provider value={{ 
      FileUploadHandler, 
      unityDataHandler, 
      isConnected: !!connection,
      connectionError
    }}>
      {children}
    </SignalRContext.Provider>
  );
};

export default function useSignalR() {
  return useContext(SignalRContext);
}
